import { Avatar, Button, Form, Input, message, Modal, Tooltip, Upload } from 'antd';
import { AvatarProps } from 'antd/lib/avatar';
import { FormComponentProps, FormItemProps } from 'antd/lib/form';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactAddType } from '../../api/contacts/model';
import FilesService, { FORM_DATA_KEY } from '../../api/files/FilesService';
import { IFile } from '../../api/files/model';
import { MimeTypes } from '../../api/shared/enums';
import { validateAntForm } from '../../helpers/ant-form';
import { getBase64 } from '../../helpers/image-helpers';
import JobCandidateService from '../../api/job-candidates/JobCandidatesService';
import { Job } from '../../api/jobs/model';
import { useAppContext } from '../../layout/AppContext';

const formItemLayout: FormItemProps = {
  labelCol: {
    xs: 24,
    sm: 6,
  },
  wrapperCol: {
    xs: 24,
    sm: 18,
  },
};

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  linkedinUrl: string;
  facebookUrl: string;
  companyName: string;
  jobTitle: string;
}

interface Props extends FormComponentProps {
  isVisible: boolean;
  onOk: (contactId: number) => void;
  onCancel: () => void;
  job: Job;
  token: string;
}

const SuggestCandidateAddContactModal: React.FC<Props> = ({
  form,
  isVisible,
  onCancel,
  onOk,
  job,
  token,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState<UploadFile>();
  const [imgPreview, setImgPreview] = useState<string>();
  const { t } = useTranslation();
  const { isLoggedIn } = useAppContext();

  const handleSubmit = async () => {
    let dbFile: IFile | undefined;

    try {
      const values = await validateAntForm<FormValues>(form);
      const {
        firstName,
        lastName,
        email,
        linkedinUrl,
        facebookUrl,
        jobTitle,
        companyName,
      } = values;
      setIsSubmitting(true);

      if (file) {
        const formData = new FormData();
        formData.append(FORM_DATA_KEY, (file as unknown) as File);
        dbFile = isLoggedIn
          ? await FilesService.add(formData)
          : await FilesService.addFilesRecommendation(formData, token);
      }

      const model: ContactAddType = {
        firstName,
        lastName,
        email,
        linkedinUrl,
        facebookUrl,
        companyName: companyName,
        jobTitle: jobTitle,
        addToContactListId: undefined,
        pictureFileId: dbFile?.id,
        matchedCustomFields: [],
      };

      const contactId = await JobCandidateService.addContactToJob(job.id, model);
      onOk(contactId);
      form.resetFields();
      setFile(undefined);
      setImgPreview(undefined);
    } catch (error) {
      if (error.message) {
        message.error(error.message);
      }
      if (dbFile) {
        isLoggedIn
          ? await FilesService.delete(dbFile.id)
          : await FilesService.deleteFileRecommendation(dbFile.key!, token);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileOnChange = async (info: UploadChangeParam) => {
    if (info.file.status === 'removed') {
      setFile(undefined);
      setImgPreview(undefined);
    } else {
      const response = await getBase64((info.file as unknown) as File);
      if (response) {
        setImgPreview(response.toString());
      }
      setFile(info.file);
    }
  };

  let title = '';

  if (isVisible) {
    title = t('Please tell us about you in order to complete the recommendation');
  }

  const avatarProps: AvatarProps = {
    className: 'avatar-upload__img',
  };

  if (imgPreview) {
    avatarProps.src = imgPreview;
  } else {
    avatarProps.icon = 'user';
  }

  const renderContent = () => {
    if (!isVisible) {
      return null;
    }

    return (
      <Form layout="horizontal">
        <Form.Item>
          <div className="avatar-upload">
            <Upload
              fileList={file ? [file] : []}
              beforeUpload={() => false}
              onChange={handleFileOnChange}
              accept={`${MimeTypes.IMAGE_JPEG}, ${MimeTypes.IMAGE_PNG}`}
              showUploadList={false}
            >
              <Avatar {...avatarProps} />

              {imgPreview && (
                <>
                  <Tooltip title={t('Remove')}>
                    <Button
                      className="avatar-upload__btn avatar-upload__btn--remove"
                      icon="delete"
                      shape="circle"
                      type="danger"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(undefined);
                        setImgPreview(undefined);
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </Upload>
          </div>
        </Form.Item>

        <Form.Item label={t('First Name')} {...formItemLayout}>
          {form.getFieldDecorator('firstName', {
            rules: [
              {
                required: true,
                whitespace: true,
                message: t('This field is required'),
              },
            ],
          })(<Input autoFocus onPressEnter={handleSubmit} />)}
        </Form.Item>
        <Form.Item label={t('Last Name')} {...formItemLayout}>
          {form.getFieldDecorator('lastName', {
            rules: [
              {
                required: true,
                whitespace: true,
                message: t('This field is required'),
              },
            ],
          })(<Input onPressEnter={handleSubmit} />)}
        </Form.Item>

        <Form.Item label={t('Email')} {...formItemLayout}>
          {form.getFieldDecorator('email', {
            rules: [
              {
                required: true,
                whitespace: true,
                message: t('This field is required'),
              },
              {
                type: 'email',
                message: t('The entered email is not valid'),
              },
            ],
          })(<Input onPressEnter={handleSubmit} />)}
        </Form.Item>
        <Form.Item label={t('Company')} {...formItemLayout}>
          {form.getFieldDecorator('companyName', {
            rules: [
              {
                whitespace: true,
                message: t('Field cannot be empty'),
              },
            ],
          })(<Input onPressEnter={handleSubmit} />)}
        </Form.Item>
        <Form.Item label={t('Position')} {...formItemLayout}>
          {form.getFieldDecorator('jobTitle', {
            rules: [
              {
                whitespace: true,
                message: t('Field cannot be empty'),
              },
            ],
          })(<Input onPressEnter={handleSubmit} />)}
        </Form.Item>
      </Form>
    );
  };

  return (
    <Modal
      visible={isVisible}
      title={title}
      onCancel={() => {
        setImgPreview(undefined);
        setFile(undefined);
        onCancel();
        form.resetFields();
      }}
      cancelButtonProps={{ disabled: isSubmitting }}
      okButtonProps={{ disabled: isSubmitting }}
      onOk={handleSubmit}
      confirmLoading={isSubmitting}
    >
      {renderContent()}
    </Modal>
  );
};

export default Form.create<Props>()(SuggestCandidateAddContactModal);
